import React, { useCallback } from 'react'
import List from 'rc-virtual-list'
import { FilterType } from '../../../modules/TodoFilter'
import { TodoItem } from '../../../modules/TodoItem'
import { TodoRenderDataItem } from '../types'
import { useFormatSettings } from './hooks/useFormatSettings'
import { useListHeight } from './hooks/useListHeight'
import TodoItemWrapper from './TodoItemWrapper'
import TodoSectionHeader from './TodoSectionHeader'
import styles from './style.module.scss'

interface TodoSectionListViewProps {
  todoRenderDataList: TodoRenderDataItem[]
  currentFilter: FilterType
  collapsedKeys: string[]
  onSectionCollapse: (keys: string[]) => void
  selectedTodo: TodoItem | null
  onTodoSelect: (todo: TodoItem | null) => void
  onImportantToggle: (todo: TodoItem, isImportant: boolean) => void
  onCompleteToggle: (todo: TodoItem, isCompleted: boolean) => void
  onTodoUpdate: () => void
  searchText?: string
}

/**
 * 섹션과 Todo 아이템을 가상 리스트로 렌더링하는 컴포넌트
 */
const TodoSectionListView: React.FC<TodoSectionListViewProps> = ({
  todoRenderDataList,
  currentFilter,
  collapsedKeys,
  onSectionCollapse,
  selectedTodo,
  onTodoSelect,
  onImportantToggle,
  onCompleteToggle,
  onTodoUpdate,
  searchText = '',
}) => {
  // 포맷 설정 (모든 TodoListItem에서 공유)
  const { timeFormat, dateFormat } = useFormatSettings()

  // 리스트 높이 계산
  const { listRef, listHeight } = useListHeight(todoRenderDataList)

  // 섹션 토글 핸들러
  const handleSectionToggle = useCallback(
    (sectionKey: string) => {
      const isCollapsed = collapsedKeys.includes(sectionKey)
      const newCollapsedKeys = isCollapsed
        ? collapsedKeys.filter((key) => key !== sectionKey)
        : [...collapsedKeys, sectionKey]
      onSectionCollapse(newCollapsedKeys)
    },
    [collapsedKeys, onSectionCollapse],
  )

  // 가상 리스트 아이템 렌더러
  const renderListItem = useCallback(
    (item: TodoRenderDataItem, _index: number, props: any) => {
      const modifiedProps = { ...props }

      // offsetX를 offsetx로 변환 (소문자로)
      if (props.offsetX != null) {
        modifiedProps.offsetx = props.offsetX
        delete modifiedProps.offsetX
      }

      // 섹션 헤더 렌더링
      if (item.type === 'section') {
        const isExpanded = !collapsedKeys.includes(item.section!.key)
        return (
          <div {...modifiedProps} className={styles.sectionItem}>
            <TodoSectionHeader
              section={item.section!}
              isExpanded={isExpanded}
              onToggle={() => handleSectionToggle(item.section!.key)}
            />
          </div>
        )
      }

      // Todo 아이템 렌더링
      return (
        <div {...modifiedProps} className={styles.todoItem}>
          <TodoItemWrapper
            item={item}
            selectedTodo={selectedTodo}
            currentFilter={currentFilter}
            onTodoSelect={onTodoSelect}
            onImportantToggle={onImportantToggle}
            onCompleteToggle={onCompleteToggle}
            onTodoUpdate={onTodoUpdate}
            searchText={searchText}
            timeFormat={timeFormat}
            dateFormat={dateFormat}
          />
        </div>
      )
    },
    [
      collapsedKeys,
      selectedTodo,
      currentFilter,
      onTodoSelect,
      onImportantToggle,
      onCompleteToggle,
      onTodoUpdate,
      searchText,
      timeFormat,
      dateFormat,
      handleSectionToggle,
    ],
  )

  return (
    <List
      data={todoRenderDataList}
      ref={listRef}
      itemHeight={54}
      height={listHeight}
      itemKey={(item) => item.key}
      style={{ height: '100%' }}
      styles={{
        verticalScrollBar: {
          width: 14,
        },
        verticalScrollBarThumb: {
          border: '3.5px solid #0000',
          width: '1px',
          backgroundColor: '#0003',
          marginLeft: '4px',
        },
      }}
    >
      {renderListItem}
    </List>
  )
}

export default TodoSectionListView
